import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ArrowLeft, ShoppingCart, ChefHat, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SpicinessBadge from '../components/SpicinessBadge';
import SafeHtml from '../components/SafeHtml';
import { fetchProductBySlug, fetchProducts } from '../data/products';
import { useCartStore } from '../store/cartStore';
import { useToastStore } from '../store/toastStore';
import { useDeliveryRegionStore } from '../store/deliveryRegionStore';
import { brandLabel, isLocalOnlyCategory } from '../lib/brandMap';
import { useNaturesStore } from '../store/naturesStore';
import { formatPrice } from '../lib/utils';
import { trackProductView, trackAddToCart } from '../lib/analytics';
import type { Product } from '../types';
// @module:imports:shipping — 選用組件 shipping-zones 的 widget import 由 compose 注入於此行之下
import ShippingQuoteWidget from '../components/ShippingQuoteWidget';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<Product[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const addItem = useCartStore((s) => s.addItem);
  const addToast = useToastStore((s) => s.addToast);
  // 地址為王（客人 2026-09-05）：收貨地區為海外時，「僅本地可售」商品不可加購
  const deliveryRegion = useDeliveryRegionStore((s) => s.deliveryRegion);
  useNaturesStore((s) => s.loaded); // shipping-zones v2 定性映射載入後 badge 重算
  const overseasBlocked = deliveryRegion === 'OS' && isLocalOnlyCategory(product?.category);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchProductBySlug(decodeURIComponent(slug)).then((p) => {
      setProduct(p);
      setSelectedImage(0);
      if (p) {
        trackProductView(p.id, p.sku, p.name);
        fetchProducts().then((all) => {
          setRelated(all.filter((x) => x.id !== p.id).slice(0, 3));
        });
      }
      setLoading(false);
    });
  }, [slug]);

  const handleAddToCart = () => {
    if (!product) return;
    if (overseasBlocked) {
      addToast(`「${product.name}」只限香港本地配送，無法寄海外`, 'error');
      return;
    }
    addItem(product, quantity);
    trackAddToCart(product.id, quantity, product.price);
    addToast(`已將 ${quantity} 件「${product.name}」加入購物車`, 'success');
  };

  // Lightbox：Esc 關閉、左右方向鍵切換、鎖背景捲動
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const imgs = product ? [product.imageUrl, product.imageUrl2, product.imageUrl3].filter(Boolean) : [];
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (imgs.length > 1) {
        if (e.key === 'ArrowLeft') setSelectedImage((i) => (i === 0 ? imgs.length - 1 : i - 1));
        if (e.key === 'ArrowRight') setSelectedImage((i) => (i === imgs.length - 1 ? 0 : i + 1));
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, product]);

  const prevImage = () => {
    setSelectedImage((i) => (i === 0 ? productImages.length - 1 : i - 1));
  };

  const nextImage = () => {
    setSelectedImage((i) => (i === productImages.length - 1 ? 0 : i + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">載入中...</div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">找不到產品</h1>
          <Link to="/products" className="btn-primary">返回產品目錄</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const productImages = [product.imageUrl, product.imageUrl2, product.imageUrl3].filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/products" className="inline-flex items-center gap-1 text-stone-600 hover:text-x1493-primary mb-6">
            <ArrowLeft size={18} />
            返回產品目錄
          </Link>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-4">
              <div
                className="relative rounded-3xl overflow-hidden bg-stone-100 aspect-square group cursor-zoom-in"
                onClick={() => productImages[selectedImage] && setLightboxOpen(true)}
                title="點擊放大"
              >
                {productImages[selectedImage] ? (
                  <img src={productImages[selectedImage]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-8xl">🍜</div>
                )}

                {productImages[selectedImage] && (
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <ZoomIn size={18} />
                  </div>
                )}

                {productImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
                      aria-label="上一張"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
                      aria-label="下一張"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {productImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setSelectedImage(idx); }}
                          className={`w-2.5 h-2.5 rounded-full transition ${
                            idx === selectedImage ? 'bg-x1493-primary' : 'bg-white/80 hover:bg-white'
                          }`}
                          aria-label={`切換到圖片 ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {productImages.length > 1 && (
                <div className="flex gap-3 justify-center">
                  {productImages.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-xl overflow-hidden bg-stone-100 border-2 transition ${
                        selectedImage === idx ? 'border-x1493-primary' : 'border-transparent hover:border-stone-300'
                      }`}
                    >
                      <img src={url} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <SpicinessBadge level={product.spicinessLevel} size="lg" />
              {product.brand && (
                <span className="inline-flex items-center gap-1 bg-x1493-primary/10 text-x1493-primary text-xs font-bold px-3 py-1 rounded-full mt-4">
                  🏪 {brandLabel(product.brand)}
                </span>
              )}
              {isLocalOnlyCategory(product.category) && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full mt-4 ml-2">
                  🇭🇰 只限香港本地配送
                </span>
              )}
              <h1 className="text-3xl md:text-4xl font-bold mt-4 mb-4">{product.name}</h1>
              <p className="text-3xl font-bold text-x1493-primary mb-6">
                {formatPrice(product.price)}
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="ml-3 text-lg text-stone-400 line-through">{formatPrice(product.originalPrice)}</span>
                )}
              </p>

              <div className="text-stone-600 leading-relaxed mb-8 whitespace-pre-line [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_a]:text-x1493-primary [&_a]:underline">
                <SafeHtml html={product.description || ''} />
              </div>

              {product.suitableDishes && product.suitableDishes.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <ChefHat size={20} className="text-x1493-secondary" />
                    適合菜式
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.suitableDishes.map((dish) => (
                      <span key={dish} className="bg-stone-100 text-stone-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                        {dish}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mb-8">
                <span className="font-bold text-stone-700">數量</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="w-10 text-center font-bold text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {overseasBlocked ? (
                <div className="w-full md:w-auto bg-stone-100 text-stone-500 font-bold rounded-xl px-6 py-3 text-center text-sm">
                  🇭🇰 只限香港本地配送——海外收貨不開放
                </div>
              ) : (
                <button
                  onClick={handleAddToCart}
                  disabled={product.stockQuantity <= 0}
                  className="w-full md:w-auto btn-primary flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={20} />
                  加入購物車
                </button>
              )}

              {
                // @module:shipping-widget — 選用組件 shipping-zones 的運費試算 widget 由 compose 注入於此行之下（空容器＝未選配零渲染）
              <ShippingQuoteWidget mode="product" productId={product.id} priceCents={product.price} />
              }
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold mb-6">你可能也會喜歡</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <ProductCard key={p.id} product={p} compact />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 圖片放大 Lightbox */}
      {lightboxOpen && productImages[selectedImage] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="放大圖片"
        >
          <button
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            aria-label="關閉"
          >
            <X size={24} />
          </button>

          {productImages.length > 1 && (
            <>
              <button
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                aria-label="上一張"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                aria-label="下一張"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}

          <img
            src={productImages[selectedImage]}
            alt={product.name}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {productImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
              {selectedImage + 1} / {productImages.length}
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
